import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import loadable from '@loadable/component';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import useContextApi from '../../hooks/useContextApi';
import ScrollToTop from '../../components/ScrollToTop';
import Header from '../../components/Header';
import LayoutContainer from '../../components/LayoutContainer';
import ReviewForm from '../../components/ReviewForm';
import AddABusiness from '../../components/AddABusiness';
import Animation from '../../components/Animation';
import ShareModal from '../../components/ShareModal';
import GmbBanner from '../../components/GmbBanner';
import EnquiryForm from '../../components/EnquiryForm';
import LazyLoad from '../../components/LazyLoad';
import QuoteForm from '../../components/QuoteForm';
import QuoteFormButton from '../../components/QuoteForm/QuoteFormButton';
import SecondaryNav from '../../components/SecondaryNav';
import CenteredLoader from '../../components/CenteredLoader';
import listviewLoader from '../../animations/listviewLoader.json';
import deconstructLocation from '../../utils/deconstructLocation';
import deparameterize from '../../utils/deparameterize';
import slugify from '../../utils/slugify';
import smoothScroll from '../../utils/smoothScroll';
import getRoute from '../../utils/getRoute';
import categoryUrl from '../../utils/categoryUrl';
import { Context } from '../../context/AppContext';
import {
  LISTVIEW_PAGE_SIZE,
  LISTVIEW_SECONDARY_HEADING,
  LISTVIEW_PRIMARY_H1,
} from '../../constants';
import HeaderSearch from './HeaderSearch';
import Filters from './Filters';
import Title from './Title';
import Cards from './Cards';
import Meta from './Meta';
import MobileCTA from './MobileCTA';
import Schema from './Schema';
import redirects from './redirects';

import './styles.css';

const Pagination = loadable(() => import('./Pagination'), {
  fallback: <CenteredLoader />,
});
const Map = loadable(() => import('./Map'), {
  fallback: <CenteredLoader />,
});
const TopServices = loadable(() => import('./TopServices'), {
  fallback: <CenteredLoader />,
});

const AverageFeature = loadable(() => import('./AverageFeature'), {
  fallback: <CenteredLoader />,
});

const SeoSection = loadable(() => import('./SeoSection'), {
  fallback: <CenteredLoader />,
});
const Faqs = loadable(() => import('./Faqs'), {
  fallback: <CenteredLoader />,
});

const Blurb = loadable(() => import('./Blurb'), {
  fallback: <CenteredLoader />,
});

const Breadcrumbs = loadable(() => import('../../components/Breadcrumbs'), {
  fallback: <CenteredLoader />,
});
const SponsoredBusinesses = loadable(
  () => import('../../components/SponsoredBusinesses'),
  {
    fallback: <CenteredLoader />,
  }
);
const LSFeatureBanner = loadable(
  () => import('../../components/LSFeatureBanner'),
  {
    fallback: <CenteredLoader />,
  }
);

const Sidebar = loadable(() => import('./Sidebar'), {
  fallback: <CenteredLoader />,
});

const SidebarSticky = loadable(() => import('./Sidebar/Sticky'), {
  fallback: <CenteredLoader />,
});

const filterPageParam = pagination => {
  if (!pagination) return 0;
  const split = pagination.split('p=');
  return split.length > 1 ? Number(split[split.length - 1]) : 0;
};

const ssr = ({ params, query, redirect }) => {
  const { slug, categoryName, pagination } = params;
  const { where, what } = query;
  const { suburb, state, postcode } = deconstructLocation(slug || where);

  const redirectUrl = redirects(params, query);

  if (redirectUrl) {
    return redirect(redirectUrl);
  }

  return [
    {
      api: 'List',
      options: {
        params: {
          keyword: categoryName || what,
          suburb,
          state,
          postcode,
          page: filterPageParam(pagination),
          size: LISTVIEW_PAGE_SIZE,
        },
      },
      callback: async data => {
        const url = redirects(params, query, data);
        return url ? redirect(url) : false;
      },
    },
  ];
};

const Listview = ({
  location: { search = '' } = {},
  match: { params: { categoryName, slug } = {} } = {},
}) => {
  const {
    media: { mobile, desktop, tablet },
    userLocation: { setLocation },
  } = useContext(Context);

  const { List: { data, loading, links, fetch } = {} } = useContextApi(ssr);

  const leftRef = useRef(null);
  const { pathname } = useLocation();
  const route = getRoute(pathname);

  const { what = categoryName, where = slug } = queryString.parse(search);

  const [page, setPage] = useState(filterPageParam(pathname));
  const [params, setParams] = useState({});
  const [keywordMetaMatch, setKeywordMetaMatch] = useState();

  const {
    breadcrumbs = [],
    meta,
    filters,
    faq,
    reviews,
    web_banners: banners,
    aggregate_review: aggregateReview,
    featured_photos: featuredPhotos,
    top_products: topProducts,
    top_services: topServices,
  } = links || {};

  const {
    location = {},
    category: metaCategory,
    category_id: categoryId,
    description,
    sub_title: subTitle,
    search_total: total,
    quotable,
    regionCitySearch,
  } = meta || {};
  const { priceRange } = filters || {};

  const {
    suburb: locationSuburb,
    state: locationState,
    postcode: locationPostcode,
  } = deconstructLocation(where);

  const {
    suburb = locationSuburb,
    state = locationState,
    postcode = locationPostcode,
    market,
    geo = '',
  } = location || {};

  const { review_count: count, average_rating: averageRating } =
    aggregateReview || {};
  const reviewAggregates = {
    averageRating,
    count,
  };

  const featureReviews = useMemo(
    () =>
      (reviews || []).map(
        ({
          uuid: id,
          rating,
          author_name: displayName,
          suburb: suburbName,
          state: stateName,
          comment,
          profile_page_hash: hash,
          business_name: businessName,
          posted_at: updatedAt,
        }) => ({
          id,
          rating,
          displayName,
          suburbName,
          state: stateName,
          message: comment,
          updatedAt,
          listing: { name: businessName, hash },
        })
      ),
    [reviews]
  );

  useEffect(() => {
    setParams({
      ...params,
      keyword: what,
      suburb,
      state,
      postcode,
      page: filterPageParam(pathname),
      size: LISTVIEW_PAGE_SIZE,
    });
  }, [what, suburb, state, postcode, pathname]);

  useEffect(() => {
    setParams({
      keyword: what,
      suburb,
      state,
      postcode,
      page: filterPageParam(pathname),
      size: LISTVIEW_PAGE_SIZE,
    });
    setPage(filterPageParam(pathname));
  }, [pathname]);

  const searchKeyword = useMemo(() => {
    if (slugify(metaCategory, /[’]/, '-') === slugify(what)) {
      setKeywordMetaMatch(true);
      return metaCategory;
    }
    setKeywordMetaMatch(false);
    return deparameterize(what);
  }, [what, metaCategory]);

  const searchLocation = useMemo(
    () => (suburb ? deparameterize(suburb) : deparameterize(what)),
    [suburb, what]
  );

  const mapData = useMemo(() => {
    if (data && !!data.length) {
      return data;
    }
    return [];
  }, [data]);

  useEffect(() => {
    if (location && location.suburb)
      setLocation({ ...location, name: location.suburb });
  }, [location, setLocation]);

  const setUrlPath = pagePath => {
    if (window && route === 'find') {
      if (pagePath === 0) {
        const newPath = `${window.location.pathname.replace(/\/p=.$/, '')}`;
        if (newPath !== window.location.pathname)
          window.history.replaceState(null, null, `${newPath}`);
      } else if (window.location.pathname.match(/\/p=.$/)) {
        const newPath = `${window.location.pathname.replace(
          /\/p=.$/,
          `/p=${pagePath}`
        )}`;
        if (newPath !== window.location.pathname)
          window.history.replaceState(null, null, `${newPath}`);
      } else {
        window.history.replaceState(
          null,
          null,
          `${window.location.pathname}/p=${pagePath}`
        );
      }
    }
  };

  const fetchMore = (e, listParams) => {
    e.preventDefault();
    const fetchParams = {
      keyword: what,
      page,
      postcode,
      size: LISTVIEW_PAGE_SIZE,
      state,
      suburb,
      ...listParams,
    };
    const { page: paramPage } = fetchParams;
    setPage(paramPage);
    setParams(fetchParams);
    setUrlPath(paramPage);
    fetch({
      params: fetchParams,
    });
    setTimeout(() => {
      smoothScroll(document.querySelector('.Listview__root'), 0);
    }, 0);
  };

  const onPageClick = (e, pageNumber) => {
    const { filter, sort } = params || {};
    fetchMore(e, {
      filter,
      sort,
      page: pageNumber - 1,
    });
  };

  return (
    <div
      className={classNames('Listview__root', {
        'Listview__root--mobile': mobile,
      })}
    >
      <ScrollToTop />
      <Schema
        data={data}
        links={links}
        reviewAggregates={reviewAggregates}
        reviews={featureReviews}
        category={searchKeyword}
        suburb={searchLocation}
        state={state}
      />
      <Meta
        {...{
          searchKeyword,
          data,
          slug,
          suburb,
          state,
          postcode,
          page: filterPageParam(pathname),
          total,
          reviewAggregates,
        }}
      />
      <Header
        searchKeyword={
          mobile ? `${searchKeyword} in ${searchLocation}` : searchKeyword
        }
        searchLocation={searchLocation}
        showLocationInput
        hideSearch={mobile}
        headerBottom={
          mobile && (
            <HeaderSearch
              searchKeyword={searchKeyword}
              searchLocation={searchLocation}
            />
          )
        }
      />
      {tablet && (
        <>
          <SecondaryNav />
          <Filters
            location={searchLocation}
            filters={filters}
            fetchMore={fetchMore}
            params={params}
            suburb={suburb}
            state={state}
            category={searchKeyword}
            pathname={pathname}
          />
        </>
      )}
      <LayoutContainer className="Listview__body">
        {loading && !data ? (
          <Animation width={300} animation={listviewLoader} />
        ) : (
          <>
            <div className="Listview__left">
              {(desktop || mobile) && (
                <Filters
                  location={searchLocation}
                  filters={filters}
                  fetchMore={fetchMore}
                  params={params}
                  suburb={suburb}
                  state={state}
                  category={searchKeyword}
                  pathname={pathname}
                />
              )}
              {data && !!data.length && (
                <Title
                  {...{
                    subTitle: keywordMetaMatch && subTitle,
                    suburb,
                    state,
                    category: searchKeyword,
                    total,
                    url: pathname,
                    share: data && !!data.length,
                    title: LISTVIEW_PRIMARY_H1({
                      keyword: searchKeyword,
                      suburb,
                      state,
                      postcode,
                    }),
                  }}
                />
              )}
              <Cards
                ref={leftRef}
                page={page}
                data={data}
                loading={loading}
                suburb={suburb}
                market={market}
                banners={banners}
                featuredPhotos={featuredPhotos}
                searchKeyword={searchKeyword}
                categoryId={categoryId}
                featureReviews={featureReviews}
                regionCitySearch={regionCitySearch}
              />
              <LazyLoad>
                <Pagination
                  total={total}
                  pageLimit={LISTVIEW_PAGE_SIZE}
                  activePage={page + 1}
                  currentTotal={data && data.length}
                  onPageClick={onPageClick}
                />
              </LazyLoad>
            </div>
            {(desktop || tablet) && (
              <div className="Listview__right">
                <Sidebar
                  style={{
                    minHeight: 0,
                  }}
                >
                  <h3 className="Listview__secondary__heading">
                    {LISTVIEW_SECONDARY_HEADING({
                      keyword: searchKeyword,
                      suburb: searchLocation,
                      state,
                      postcode,
                    })}
                  </h3>
                  {data && !!data.length && (
                    <>
                      <Map data={mapData} mapCenter={geo} />
                      {!loading && (
                        <SidebarSticky>
                          <SponsoredBusinesses
                            className="Listview__Sidebar__SponsoredBusinesses"
                            touchEnabled={false}
                            dragEnabled={false}
                            data={banners}
                            target="_blank"
                          />
                        </SidebarSticky>
                      )}
                      {((reviewAggregates &&
                        !!reviewAggregates.averageRating) ||
                        (priceRange && !!priceRange.length)) && (
                        <AverageFeature
                          priceRange={priceRange}
                          reviewAggregates={reviewAggregates}
                          title={`${searchKeyword} in ${searchLocation}`}
                          total={total}
                          category={searchKeyword}
                          hasFeatureReviews={
                            featureReviews && !!featureReviews.length
                          }
                        />
                      )}
                    </>
                  )}

                  <GmbBanner type="growBusiness" />
                  <div
                    className="Listview__spacer"
                    style={{ height: '100px' }}
                  />
                </Sidebar>
              </div>
            )}
          </>
        )}
      </LayoutContainer>

      {data && (
        <>
          {mobile &&
            ((reviewAggregates && !!reviewAggregates.averageRating) ||
              (priceRange && !!priceRange.length)) && (
              <LazyLoad>
                <AverageFeature
                  priceRange={priceRange}
                  reviewAggregates={reviewAggregates}
                  title={`${searchKeyword} in ${searchLocation}`}
                  total={total}
                  category={searchKeyword}
                  hasFeatureReviews={featureReviews && !!featureReviews.length}
                />
              </LazyLoad>
            )}
          <LazyLoad style={{ minHeight: '100px' }}>
            <LSFeatureBanner
              utm={{
                campaign: 'list_view_digital_marketing_banner',
              }}
              style={{
                background: 'none',
                padding: '8px 0 20px',
              }}
            />
          </LazyLoad>
          <LazyLoad style={{ minHeight: '200px' }}>
            {!!categoryId && (
              <SeoSection
                {...{
                  suburb,
                  category: searchKeyword,
                  state,
                  slug,
                  postcode,
                  categoryId,
                }}
              />
            )}
          </LazyLoad>
          <LazyLoad style={{ minHeight: '200px' }}>
            {(!!(topProducts || []).length || !!(topServices || []).length) && (
              <LayoutContainer className="Listivew__TopServices__Container">
                {topProducts && !!topProducts.length && (
                  <TopServices
                    data={topProducts}
                    title={`Top Products offered for ${searchKeyword} in ${suburb} ${state}${
                      postcode ? ` ${postcode}` : ''
                    }`}
                  />
                )}
                {topServices && !!topServices.length && (
                  <TopServices
                    data={topServices}
                    title={`Top Services offered for ${searchKeyword} in ${suburb} ${state}${
                      postcode ? ` ${postcode}` : ''
                    }`}
                  />
                )}
              </LayoutContainer>
            )}
          </LazyLoad>
          {faq && !!faq.length && (
            <LazyLoad style={{ minHeight: '100px' }}>
              <Faqs
                category={searchKeyword}
                data={faq}
                suburb={suburb}
                state={state}
                postcode={postcode}
              />
            </LazyLoad>
          )}
          {keywordMetaMatch && !!description && (
            <Blurb
              suburb={suburb}
              blurb={description}
              quotable={quotable}
              category={searchKeyword}
              breadcrumbs={[
                {
                  breadcrumb: 'Localsearch',
                  url: '/',
                },
                {
                  breadcrumb: `${searchKeyword}`,
                  url: categoryUrl(searchKeyword),
                },
                ...(breadcrumbs || []),
                {
                  breadcrumb: `${searchKeyword} in ${searchLocation}`,
                },
              ]}
            />
          )}
          {(mobile || tablet) && (
            <>
              {data && !!data.length && (
                <LayoutContainer className="Listview__titleBottom">
                  <Title
                    {...{
                      suburb,
                      state,
                      category: searchKeyword,
                      url: pathname,
                      share: data && !!data.length,
                      title: LISTVIEW_SECONDARY_HEADING({
                        keyword: searchKeyword,
                        suburb: searchLocation,
                        state,
                        postcode,
                      }),
                    }}
                  />
                </LayoutContainer>
              )}
            </>
          )}
          {!(keywordMetaMatch && !!description) && (
            <LayoutContainer className="Listview__Breadcrumbs">
              <Breadcrumbs
                data={[
                  {
                    breadcrumb: 'Localsearch',
                    url: '/',
                  },
                  ...(keywordMetaMatch
                    ? [
                        {
                          breadcrumb: `${searchKeyword}`,
                          url: categoryUrl(searchKeyword),
                        },
                      ]
                    : []),
                  ...(breadcrumbs || []),
                  {
                    breadcrumb: `${searchKeyword} in ${searchLocation}`,
                  },
                ]}
              />
            </LayoutContainer>
          )}
        </>
      )}
      {mobile && quotable && <MobileCTA quotable={quotable} />}
      <ReviewForm />
      <ShareModal />
      <EnquiryForm />
      <QuoteForm
        defaults={{
          categoryId,
          category: searchKeyword,
        }}
      />
      {(desktop || tablet) && !!quotable && categoryId && <QuoteFormButton />}
      <AddABusiness />
    </div>
  );
};

export { ssr };

Listview.defaultProps = {
  match: {
    params: {},
  },
};

export default Listview;
